import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UploadSimple, FloppyDisk, Trash, IdentificationBadge } from "@phosphor-icons/react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import HoverTip from "@/components/HoverTip";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EMPTY = {
  advisor_name: "",
  firm_name: "",
  license_number: "",
  email: "",
  phone: "",
  website: "",
  tagline: "",
  logo_data_url: "",
  calendly_url: "",
  notification_email: "",
  notifications_enabled: true,
  hot_lead_threshold: 500000,
  annual_premium_goal: 0,
};

export default function Settings() {
  const [profile, setProfile] = useState(EMPTY);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/advisor`);
        if (active) setProfile({ ...EMPTY, ...data });
      } catch (e) {
        if (active) toast.error("Could not load profile");
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, []);

  const update = (k, v) => setProfile((p) => ({ ...p, [k]: v }));

  const onLogo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 600_000) {
      toast.error("Logo too large", { description: "Please use an image under 600 KB." });
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => update("logo_data_url", ev.target.result);
    reader.readAsDataURL(file);
  };

  const removeLogo = () => update("logo_data_url", "");

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/advisor`, profile);
      toast.success("Profile saved", {
        description: "Your branding will appear on every PDF cover page.",
      });
    } catch (e) {
      toast.error("Save failed", { description: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  return (
    <main data-testid="settings-page" className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10 md:py-16">
      <div className="mb-10">
        <span className="label-eyebrow">Advisor branding</span>
        <h1 className="mt-3 font-serif text-4xl md:text-5xl text-brand-forest leading-tight">
          Your profile
        </h1>
        <p className="mt-3 text-brand-ink/70 max-w-xl">
          This is what your clients see on the cover of every PDF you generate. Add a logo,
          your contact details, and a tagline. We auto-attach this to all exports.
        </p>
      </div>

      {loading ? (
        <div className="bg-white border border-brand-line rounded-md p-8 h-96 animate-pulse" />
      ) : (
        <div className="bg-white border border-brand-line rounded-md p-6 md:p-8 space-y-7">
          {/* Logo upload */}
          <div>
            <HoverTip id="logo" label="Firm logo" />
            <div className="mt-3 flex items-center gap-5">
              <div className="w-24 h-24 rounded-md border border-brand-line bg-brand-cream/40 flex items-center justify-center overflow-hidden shrink-0">
                {profile.logo_data_url ? (
                  <img
                    src={profile.logo_data_url}
                    alt="Logo preview"
                    className="w-full h-full object-contain"
                    data-testid="logo-preview"
                  />
                ) : (
                  <IdentificationBadge size={32} weight="duotone" className="text-brand-muted" />
                )}
              </div>
              <div className="flex flex-col gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  data-testid="btn-upload-logo"
                  className="border-brand-line text-brand-forest hover:bg-brand-cream rounded-md gap-2"
                >
                  <UploadSimple size={16} weight="bold" />
                  {profile.logo_data_url ? "Replace logo" : "Upload logo"}
                </Button>
                {profile.logo_data_url && (
                  <button
                    type="button"
                    onClick={removeLogo}
                    data-testid="btn-remove-logo"
                    className="text-xs text-brand-muted hover:text-red-700 inline-flex items-center gap-1.5 transition-colors"
                  >
                    <Trash size={12} weight="bold" /> Remove
                  </button>
                )}
                <p className="text-xs text-brand-muted">PNG or JPG, ideally square, under 600 KB.</p>
              </div>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg"
                onChange={onLogo}
                className="hidden"
                data-testid="input-logo-file"
              />
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-5">
            <Field label="Advisor name" id="advisor_name" value={profile.advisor_name} onChange={(v) => update("advisor_name", v)} placeholder="Sarah Williams, CFP" />
            <Field label="Firm name" id="firm_name" value={profile.firm_name} onChange={(v) => update("firm_name", v)} placeholder="North Bay Wealth Inc." />
            <Field label="Email" id="email" type="email" value={profile.email} onChange={(v) => update("email", v)} placeholder="sarah@northbaywealth.ca" />
            <Field label="Phone" id="phone" value={profile.phone} onChange={(v) => update("phone", v)} placeholder="(705) 555-0188" />
            <Field label="Website" id="website" value={profile.website} onChange={(v) => update("website", v)} placeholder="northbaywealth.ca" />
            <Field label="Licence # (optional)" id="license_number" value={profile.license_number} onChange={(v) => update("license_number", v)} placeholder="LLQP-12345" />
          </div>

          <Field
            label="Tagline (optional)"
            id="tagline"
            value={profile.tagline}
            onChange={(v) => update("tagline", v)}
            placeholder="IBC strategies for Ontario contractors"
          />

          <div>
            <Field
              label="Booking link (Calendly)"
              id="calendly_url"
              value={profile.calendly_url}
              onChange={(v) => update("calendly_url", v)}
              placeholder="https://calendly.com/your-name/discovery-call"
            />
            <p className="mt-2 text-xs text-brand-muted">
              Added as a "Book a call" button in the follow-up email every prospect receives —
              hot leads can schedule themselves before you even pick up the phone.
            </p>
          </div>

          {/* Notifications */}
          <div className="pt-6 border-t border-brand-line">
            <div className="flex items-baseline justify-between gap-3 mb-4">
              <HoverTip id="notifications_enabled" label="Lead notifications" />
              <label className="flex items-center gap-2 text-xs text-brand-ink/70 cursor-pointer">
                <input
                  type="checkbox"
                  data-testid="input-notifications_enabled"
                  checked={profile.notifications_enabled}
                  onChange={(e) => update("notifications_enabled", e.target.checked)}
                  className="rounded border-brand-line text-brand-forest focus:ring-brand-forest"
                />
                Enabled
              </label>
            </div>
            <Field
              label="Send 'new lead' emails to"
              id="notification_email"
              type="email"
              value={profile.notification_email}
              onChange={(v) => update("notification_email", v)}
              placeholder="alerts@yourfirm.ca"
            />
            <p className="mt-2 text-xs text-brand-muted">
              Falls back to your main email if blank. The moment a prospect submits the public
              page, you'll get a styled email with their teaser figure and contact details.
              Requires Resend API key configured server-side.
            </p>

            <div className="mt-5">
              <Field
                label="Hot lead threshold (10-yr projection)"
                id="hot_lead_threshold"
                type="number"
                value={profile.hot_lead_threshold}
                onChange={(v) => update("hot_lead_threshold", v === "" ? 0 : Number(v))}
                placeholder="500000"
              />
              <p className="mt-2 text-xs text-brand-muted">
                Any lead whose 10-year projection lands at or above this figure triggers an extra
                "HOT LEAD" email so you can call them the same day. Set to 0 to switch it off.
                You'll also get a morning reminder about any lead still open after three days.
              </p>
            </div>

            <div className="mt-5">
              <Field
                label="Annual premium goal"
                id="annual_premium_goal"
                type="number"
                value={profile.annual_premium_goal}
                onChange={(v) => update("annual_premium_goal", v === "" ? 0 : Number(v))}
                placeholder="250000"
              />
              <p className="mt-2 text-xs text-brand-muted">
                Your target for annual premium written this calendar year. A progress bar appears on your
                Leads dashboard once you set it. Leave at 0 to hide it.
              </p>
            </div>
          </div>

          <div className="pt-4 border-t border-brand-line flex justify-end">
            <Button
              onClick={save}
              disabled={saving}
              data-testid="btn-save-profile"
              className="bg-brand-forest hover:bg-brand-forestHover text-brand-bone rounded-md gap-2 h-11 px-6"
            >
              <FloppyDisk size={16} weight="bold" /> {saving ? "Saving…" : "Save profile"}
            </Button>
          </div>
        </div>
      )}
    </main>
  );
}

function Field({ label, id, value, onChange, type = "text", placeholder }) {
  return (
    <div className="flex flex-col gap-2">
      <HoverTip id={id} label={label} htmlFor={id} />
      <Input
        id={id}
        data-testid={`input-${id}`}
        type={type}
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="bg-white border-brand-line focus-visible:ring-2 focus-visible:ring-brand-forest rounded-md h-11"
      />
    </div>
  );
}
